import React from 'react';
import clsx from 'clsx';

import './MaterialIcon.css';

//
// --- Types ---

/** not all included; add as needed from @see https://fonts.google.com/icons */
type MaterialIconName =
  | 'account_tree'
  | 'add'
  | 'arrow_drop_down'
  | 'arrow_drop_up'
  | 'arrow_left'
  | 'arrow_right'
  | 'cancel'
  | 'check_circle'
  | 'clear'
  | 'close'
  | 'compress'
  | 'contact_mail'
  | 'crop_din'
  | 'delete'
  | 'done'
  | 'edit'
  | 'error'
  | 'expand'
  | 'filter_none'
  | 'info'
  | 'list'
  | 'mail'
  | 'mediation'
  | 'person_add'
  | 'search'
  | 'send'
  | 'sort'
  | 'sort_by_alpha'
  | 'star'
  | 'star_border'
  | 'thumbs_down'
  | 'thumbs_up'
  | 'warning'
  | 'priority_high';

/** setup support thats similar to Material UI's Icon component for colors */
type MaterialIconColor = 'action' | 'disabled' | 'highlight' | 'primary' | 'secondary' | 'success' | 'tertiary';

export interface MaterialIconProps extends React.HTMLProps<HTMLElement> {
  className?: string;
  color?: MaterialIconColor;
  'data-testid'?: string;
  name: MaterialIconName;
}

//
// --- MaterialIcon Component ---

/**
 * Based on Google's material icons. Requires icon font included in app's index.html.
 * Font uses ligatures to determine which icon to show
 *
 * Search Icon Library:
 * @see https://fonts.google.com/icons
 *
 * Developer Guide:
 * @see https://developers.google.com/fonts/docs/material_icons
 *
 * More about Ligatures:
 * @see https://alistapart.com/article/the-era-of-symbol-fonts/
 */

const MaterialIcon = React.forwardRef<HTMLElement, MaterialIconProps>((props, ref) => {
  const { className, color, 'data-testid': dataTestId, name, ref: propRef, ...remainingIElementProps } = props;

  return (
    <i
      className={clsx(
        'material-icons', // class name required by google api
        'material-icon', // class name matching component name
        {
          'material-icon-color-action': color === 'action',
          'material-icon-color-disabled': color === 'disabled',
          'material-icon-color-highlight': color === 'highlight',
          'material-icon-color-primary': color === 'primary',
          'material-icon-color-secondary': color === 'secondary',
          'material-icon-color-success': color === 'success',
          'material-icon-color-tertiary': color === 'tertiary'
        },
        className
      )}
      data-testid={dataTestId || `material-icon-${name}`}
      ref={ref}
      {...remainingIElementProps}
    >
      {name}
    </i>
  );
});

export default MaterialIcon;
